import get from 'lodash.get';
import {
    USER_TYPES, ACCESS_DENIED_PATH, ERROR_PATH, TRANSACTION_PATH, TEMPLATE_PATH, LOGIN_PATH, SENDER_AUTH_PATH
} from '../../constants';
import PERMISSIONS from './permissionsConstants';
import {getScopedTransactionId} from './jwtUtils';
import {getIsSender} from './userRole';

export function hasPermission(permissions = [], key) {
    return permissions.includes(key);
}

function hasFeature(features = {}, key) {
    return get(features, key, false);
}

const permissionsService = {
    hasSubscriptionPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.sender_admin.subscription),

    hasReportPagePermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.sender_admin.reports),

    hasCustomFieldPermission: ({permissions, features}) => hasPermission(permissions, PERMISSIONS.sender_admin.custom_fields)
        && hasFeature(features, 'customFields'),

    hasEventNotificationPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.sender_admin.event_notification),

    hasCallbackReplayPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.sender_admin.event_notification),

    hasChangeSignerPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.transaction.change_signer),

    hasCustomizationPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.sender_admin.customization),

    hasBrandsPermission: ({permissions, features}) => hasPermission(permissions, PERMISSIONS.sender_admin.customization)
        && hasFeature(features, 'brands'),

    hasUserManagementPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.sender_admin.users),

    hasDelegationsPermission: ({permissions, features}) => hasPermission(permissions, PERMISSIONS.sender_admin.delegations)
        && hasFeature(features, 'delegation'),

    isAdminPermission: (permission) => permission.includes(PERMISSIONS.groups.heading)
        || (permission !== PERMISSIONS.sender_admin.reports && permission.includes(PERMISSIONS.sender_admin.heading)),

    hasAdminPermission: ({permissions}) => permissions.filter((permission) => permissionsService.isAdminPermission(permission)).length > 0,

    hasShareTemplatePermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.templates_layouts.share_templates),

    hasInPersonSigningPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.transaction.in_person),

    hasShareLayoutPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.templates_layouts.share_layouts),

    hasPasswordSettingsPermission: ({permissions, features}) => hasPermission(permissions, PERMISSIONS.sender_admin.security_settings)
        && !hasFeature(features, 'ssoLogin'),

    hasTransactionPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.transaction.transaction),

    hasArchivePermission: ({permissions, features}) => hasPermission(permissions, PERMISSIONS.transaction.archive)
        && hasFeature(features, 'archiveModule'),

    hasManageOtherUsersTransactionsPermission: ({permissions}) => hasPermission(
        permissions,
        PERMISSIONS.transaction.manage_other_users_transactions
    ),

    hasRolesPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.sender_admin.role),

    hasApiAccessPermissions: ({permissions, features}) => hasPermission(permissions, PERMISSIONS.sender_admin.api_access)
        && (hasFeature(features, 'apiKey') || hasFeature(features, 'apiToken')),

    hasGroupManagementPermission: ({permissions}) => hasPermission(permissions, PERMISSIONS.groups.group_signing_management),

    hasAccountGroupManagementPermission: ({user = {}, features}) => {
        const isManager = user.type === USER_TYPES.MANAGER;
        const hasMembership = !!user.memberships?.length;
        const isFeatureEnabled = hasFeature(features, 'groups');
        const isGroupManager = user.memberships?.some(({memberType}) => memberType.toLowerCase() === 'manager');

        return !isManager && hasMembership && isFeatureEnabled && isGroupManager;
    },

    isEmailDeliveryEnabled: ({features}) => hasFeature(features, 'deliverDocumentsByEmail'),

    isEmailAliasEnabled: ({features}) => hasFeature(features, 'emailAlias'),

    hasAssignDelegatedPermission: ({user}) => get(user, 'data.isAssignDelegatesAllowed', true),
    hasUploadSignaturePermission: ({user}) => get(user, 'data.personal_preferences', {}).fromFile,

    isAllowedScope: ({accessToken, urlParams}) => {
        if (!getIsSender(accessToken)) return true;
        const scopedTransactionId = getScopedTransactionId(accessToken);
        if (!scopedTransactionId) return true;
        if (!urlParams?.packageId) return false;
        return urlParams.packageId === scopedTransactionId;
    },

    getIsScopedUser: ({accessToken}) => {
        if (!getIsSender(accessToken)) return false;
        return getScopedTransactionId(accessToken) !== null;
    },

    getIsPathAllowedForScopedUser: ({pathname, accessToken}) => {
        const scopedTransactionId = getScopedTransactionId(accessToken);
        if (!scopedTransactionId) return true;
        const allowedPrefixes = [
            `${TRANSACTION_PATH}/${scopedTransactionId}`,
            `${TEMPLATE_PATH}/${scopedTransactionId}`
        ];
        const allowedExactPaths = [
            ACCESS_DENIED_PATH,
            ERROR_PATH,
            LOGIN_PATH,
            SENDER_AUTH_PATH
        ];
        if (allowedExactPaths.includes(pathname)) return true;
        return allowedPrefixes.some((prefix) => pathname.startsWith(prefix));
    },

    isNotDelegating: ({isDelegation}) => !isDelegation

};

export default permissionsService;
